import { Injectable } from '@angular/core';
import { environment } from '@tytapp/environment';
import { Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';

export interface Message {
    type: string;
}

export interface PlatformCapabilitiesMessage {
    type: 'platform_capabilities',
    platform: 'ios' | 'android' | 'appletv' | 'androidtv' | 'roku';
    bundle_identifier: string;
    version: string;
    build_type: 'production' | 'staging';
    capabilities: string[];
    settings: Record<string,any>;
}

export interface NavigateMessage {
    type: 'navigate';
    url: string;
}

/**
 * Implements the TYT web core host API, as described in
 * https://docs.google.com/document/d/1aKhepTmweYx9d1quRqXtCOgX1l63L8cdj_tkbo8adOA/edit?usp=sharing
 *
 * This is used to enable TYT.com to be embedded within a native application, either directly from the web or by
 * including the TYT.com build within the app's distribution.
 */
@Injectable()
export class HostApi {
    constructor() {
        let enableApi = false;

        if (typeof window !== 'undefined') {
            let query = window.location.search
                .slice(1)
                .split('&')
                .map(kv => kv.split('='))
                .reduce((o, [k, v]) => (o[k] = v, o), {})
            ;

            if (query['hosted'] === '1') {
                if (environment.name === 'production') {
                    // SECURITY: It is essential that the production version of this API only
                    // be available from https://mobile.tyt.com, which is not a deployed copy of the
                    // app. That URL is used within the native app shells. This guard prevents credential
                    // stealing by malicious sites which host TYT.com in an iframe and pretend to be the
                    // mobile app by implementing the host API.

                    enableApi = window.location.origin === 'https://mobile.tyt.com';
                } else {
                    // The staging/development versions allow the API regardless of where it is loaded from.
                    enableApi = true;
                }
            }
        }

        this.apiEnabled = enableApi;

        if (this.apiEnabled) {
            window.addEventListener('message', ev => {
                if (!this.apiEnabled)
                    return;
                this._messageReceived.next(ev.data);
            });
        }
    }

    apiEnabled = false;

    sendMessage<T extends Message = Message>(message: T) {
        if (!this.apiEnabled)
            return;
        window.postMessage(message, '*');
    }

    async handshake(): Promise<PlatformCapabilitiesMessage> {
        if (!this.apiEnabled)
            return undefined;

        return new Promise((resolve, reject) => {
            let timeout = setTimeout(() => {
                reject(new Error("The operation timed out."));
            }, 10*1000);

            this.messageReceived.pipe(filter(v => v.type === 'platform_capabilities')).pipe(take(1)).subscribe(m => {
                clearTimeout(timeout);
                resolve(m as PlatformCapabilitiesMessage);
            });

            this.sendMessage({ type: 'ready' });
        });
    }

    private _messageReceived = new Subject<Message>();

    get messageReceived() { return this._messageReceived.asObservable(); }
}